import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";
import { ARCHIVE_PRODUCT, PRODUCTS, SETTINGS } from "./api";

export default function CustomLibraryProductLink({
  product,
  actions = false,
}: {
  product: any;
  actions?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [failure, setFailure] = useState("");
  const { data } = useQuery(SETTINGS);
  const [archive, { loading }] = useMutation(ARCHIVE_PRODUCT);
  const href = getRoute("product-types.custom-products.update", [
    ["productTypeId", product.organizationProductType.id],
    ["productId", product.id],
  ]);
  if (!actions)
    return (
      <Link style={{ color: "inherit" }} to={href}>
        [Custom] {product.definition.internalName || product.definition.name}
      </Link>
    );
  if (!data?.customSettings.canManageProducts) return null;
  return (
    <>
      <IconButton
        aria-label={`Actions for ${product.definition.internalName || product.definition.name}`}
        size="small"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ display: "flex", mx: "auto" }}
      >
        <Icon>more_vert</Icon>
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem component={Link} to={href} onClick={() => setAnchor(null)}>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setFailure("");
            setConfirm(true);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <Dialog open={confirm} onClose={() => !loading && setConfirm(false)}>
        <DialogTitle>Delete product?</DialogTitle>
        <DialogContent>
          Delete “{product.definition.internalName || product.definition.name}” from the library?
          Existing estimates keep their saved copies.
          {failure && <Alert severity="error">{failure}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={loading}
            onClick={async () => {
              try {
                const result = await archive({
                  variables: { id: product.id },
                  refetchQueries: [PRODUCTS],
                  awaitRefetchQueries: true,
                });
                if (!result.data?.archiveCustomProduct)
                  throw new Error("Unable to delete this product.");
                setConfirm(false);
              } catch (e) {
                setFailure((e as Error).message);
              }
            }}
          >
            {loading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
